import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FabricCanvas } from '../../types/fabric';
import { useToast } from '../Notifications';
import { BackgroundColorTool } from './BackgroundColorTool';
import { importCatalogAssetAsUpload, type StagedImage } from './StagedImage';
import { Spinner } from '../Spinner';

interface ImageCorrectionModalProps {
  canvas: FabricCanvas | null;
  /** The already-placed Fabric image object being corrected. */
  existingObject: any;
  onClose: () => void;
}

// Opened from PropertyPanel.tsx's "Correct Colors" button — re-enters the
// SAME step-by-step wizard (BackgroundColorTool) a fresh upload goes
// through, but for an image that's already a real object on the canvas.
// Per direct user request, step 1 always starts from the untouched ORIGINAL
// upload (existingObject.originalUploadUrl), never from the already-edited
// canvas image — so re-running "Remove Background" on a photo that already
// had it removed doesn't compound onto its own prior output. Closing this
// modal at any point before the wizard's final "Update Image" action leaves
// existingObject completely untouched (see BackgroundColorTool's
// existingObject commit path).
export function ImageCorrectionModal({ canvas, existingObject, onClose }: ImageCorrectionModalProps) {
  const { t } = useTranslation();
  const showToast = useToast();
  // originalUploadId/originalUploadUrl are stamped on every placed image by
  // BackgroundColorTool's own commitToCanvas (both the fresh-placement and
  // update paths) and survive save/load/autosave — see canvasObjectHelpers.ts
  // usage and the toJSON() property whitelists in useAutoSave.ts/CartPopup.tsx/
  // DesignerHeader.tsx/designDownload.ts/historyStore.ts. An object placed
  // before that stamping existed has neither, so this falls back to the
  // object's own current src as the best available "original" to start from.
  const originalUrl: string = existingObject?.originalUploadUrl ?? existingObject?.getSrc?.() ?? '';
  // A TEMPLATE-sourced object is the common real case for an UNTRUSTWORTHY
  // originalUploadId (present, but not necessarily absent) — a template's
  // jsonObjects were captured from WHOEVER'S canvas originally created it
  // (see designStore.ts's loadFromTemplate, which tags every restored image
  // `fromTemplate: true` for exactly this reason), so any originalUploadId
  // baked into that JSON belongs to that original session's customer/guest
  // identity, not necessarily the one applying the template now. Treated the
  // same as "absent" below — re-import rather than trust it.
  const realUploadId: string | undefined =
    existingObject?.originalUploadId && !existingObject?.fromTemplate
      ? existingObject.originalUploadId
      : undefined;

  // Every wizard action (vectorize/remove-background/palette/quantize-colors/
  // remove-color/recolor-swatch) calls `/api/images/${staged.uploadId}/...`,
  // which needs a REAL backend UploadedImage row this session can actually
  // access — a synthetic id (the Fabric object's own `.id`, or the literal
  // 'existing', as this fell back to previously) 404s on every single
  // request, and so does a real-but-foreign id inherited from a template (see
  // realUploadId above — loadUploadedImage's ownership check on the backend
  // rejects a customerId mismatch). Confirmed live: opening "Correct Colors"
  // on a template-placed image ("Madras-Tiger") spammed a "Color detection
  // failed" toast repeatedly (see BackgroundColorTool.tsx's
  // detectColorsForStep — that retry-storm is fixed separately, but the
  // underlying 404 needed a real fix here). Same "import an already-hosted
  // asset as a fresh UploadedImage" pattern ClipartTool.tsx/ArtworkTool.tsx/
  // ShapesTool.tsx already use for the identical problem (placing a catalog
  // asset, not a fresh upload, into this same wizard) — reused as-is via
  // importCatalogAssetAsUpload, no backend changes needed. Only runs when
  // there's no TRUSTED uploadId already; a normally-uploaded/corrected
  // image's originalUploadId is reused as-is.
  const [staged, setStaged] = useState<StagedImage | null>(
    realUploadId ? { uploadId: realUploadId, originalUrl, currentUrl: originalUrl } : null,
  );
  const [importFailed, setImportFailed] = useState(false);

  useEffect(() => {
    if (realUploadId || !originalUrl) return;
    let cancelled = false;
    (async () => {
      const result = await importCatalogAssetAsUpload(originalUrl);
      if (cancelled) return;
      if (!result) {
        setImportFailed(true);
        showToast(t('bgColor.detectFailed'), 'error');
        return;
      }
      setStaged(result);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realUploadId, originalUrl]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  if (!originalUrl || importFailed) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
    >
      <div onClick={e => e.stopPropagation()} style={{
        background: 'white', borderRadius: 10, width: 'min(360px, 100%)', maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
          <strong style={{ fontSize: 14 }}>{t('properties.correctColors')}</strong>
          <button onClick={onClose} title={t('common.close')}
            style={{ border: 0, background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '2px 8px' }}>
            ×
          </button>
        </div>

        <div
  className="color-correction-scroll"
  style={{
    padding: 12,
    overflowY: 'auto',
    minHeight: 0,
    scrollbarWidth: 'thin',
    scrollbarColor: '#b8bec7 transparent',
  }}
>
          {staged ? (
            // key={staged.uploadId} mirrors UploadTool.tsx's own remount
            // guard — opening the correction modal on a DIFFERENT object
            // while one is already open (not currently reachable via the
            // UI, but cheap insurance) must not resume the previous
            // object's stale wizard state (step index, crop insets,
            // quantized palette, ...).
            <BackgroundColorTool
              key={staged.uploadId}
              canvas={canvas}
              staged={staged}
              existingObject={existingObject}
              onDone={onClose}
            />
          ) : (
            // realUploadId was absent (template-placed object — see the
            // import effect above) and the import-as-upload call is still
            // in flight.
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 24, color: 'var(--text-muted)', fontSize: 13 }}>
              <Spinner size={14} />
              {t('properties.preparingImage')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
